<template>
  <div class="bim-rooms">
    <div class="top-bar">
      <PageTitle hideMarginLeft>Комнаты BIM</PageTitle>
    </div>

    <div class="header">
      <span class="header__count">Всего: {{ rooms.length }}</span>
      <div class="header__actions">
        <input
          ref="fileInput"
          type="file"
          accept=".xlsx,.xls,.csv"
          class="hidden-input"
          @change="onFileSelected"
        />
        <CustomButton
          :loading="isImporting"
          outlined
          @click="triggerFileInput"
        >
          Импорт из Excel/CSV
        </CustomButton>
      </div>
    </div>

    <CustomTable
      title="Помещения BIM"
      :rowsData="rooms"
      :columns="columns"
      :loading="isLoading"
      :small="true"
      :show-search="true"
      :custom-body="true"
      :sticky-header="true"
      :height="400"
    >
      <template #customBody="props">
        <q-tr>
          <custom-table-cell align="center" style="width: 80px;">
            {{ props.row.bim_room_id }}
          </custom-table-cell>

          <custom-table-cell align="left" style="min-width: 280px;">
            {{ props.row.bim_room_name }}
          </custom-table-cell>

          <custom-table-cell align="left" style="min-width: 220px;">
            <CustomSelect
              :options="roomOptions"
              optionLabelKey="room_name"
              optionValueKey="room_id"
              placeholder="Выберите комнату"
              :value="rowRoomIds[props.row.bim_room_id] ?? props.row.room_id ?? ''"
              :loading="isLoadingRoomDict"
              :clearable="true"
              dense
              @change="(value) => onRoomChange(props.row, value)"
            />
          </custom-table-cell>

          <custom-table-cell align="center" style="width: 100px;">
            <q-btn
              icon="save"
              flat
              dense
              round
              size="sm"
              color="primary"
              :loading="savingIds[props.row.bim_room_id]"
              :disable="!hasRowChanges(props.row)"
              @click="saveRow(props.row)"
            >
              <q-tooltip>Сохранить</q-tooltip>
            </q-btn>
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>
  </div>
</template>

<script>
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import CustomButton from "@/components/common/CustomButton.vue";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import CustomSelect from "@/components/form/CustomSelect";
import PageTitle from "@/components/common/PageTitle.vue";
import {
  getBimRooms,
  getAllRooms,
  saveBimRoom,
  importBimRoomsExcel,
} from "@/views/BimRooms/services";

export default {
  name: "BimRooms",
  components: {
    CustomButton,
    CustomTable,
    CustomTableCell,
    CustomSelect,
    PageTitle,
  },
  data() {
    return {
      rooms: [],
      roomOptions: [],
      rowRoomIds: {},
      savingIds: {},
      isLoading: false,
      isLoadingRoomDict: false,
      isImporting: false,
      columns: [
        {
          name: "bim_room_id",
          field: "bim_room_id",
          label: "ID",
          align: "center",
          style: "width: 80px",
        },
        {
          name: "bim_room_name",
          field: "bim_room_name",
          label: "Помещение (BIM)",
          align: "left",
          style: "min-width: 280px",
        },
        {
          name: "room_id",
          field: "room_id",
          label: "Комната (цифровой проект)",
          align: "left",
          style: "min-width: 220px",
        },
        {
          name: "actions",
          field: "actions",
          label: "",
          align: "center",
          style: "width: 100px",
        },
      ],
    };
  },
  setup() {
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();
    return { showSucceedSnackbar, showErrorSnackbar };
  },
  mounted() {
    this.loadData();
    this.loadRoomDict();
  },
  methods: {
    syncRowRoomIds() {
      const map = {};
      this.rooms.forEach((row) => {
        map[row.bim_room_id] = row.room_id ?? null;
      });
      this.rowRoomIds = map;
    },
    hasRowChanges(row) {
      const current = this.rowRoomIds[row.bim_room_id] ?? null;
      const original = row.room_id ?? null;
      return String(current ?? "") !== String(original ?? "");
    },
    onRoomChange(row, value) {
      this.rowRoomIds = {
        ...this.rowRoomIds,
        [row.bim_room_id]: value || null,
      };
    },
    async loadData() {
      this.isLoading = true;
      const data = await getBimRooms();
      this.rooms = data || [];
      this.syncRowRoomIds();
      this.isLoading = false;
    },
    async loadRoomDict() {
      this.isLoadingRoomDict = true;
      const data = await getAllRooms();
      this.roomOptions = data || [];
      this.isLoadingRoomDict = false;
    },
    triggerFileInput() {
      this.$refs.fileInput?.click();
    },
    async onFileSelected(event) {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;

      const formData = new FormData();
      formData.append("excel_file", file);

      this.isImporting = true;
      try {
        const res = await importBimRoomsExcel(formData);
        const stats = res?.stats || {};
        const inserted = stats.inserted_cnt ?? 0;
        const skipped = stats.skipped_cnt ?? 0;
        this.showSucceedSnackbar(
          res?.message ||
            `Добавлено: ${inserted}, пропущено (уже есть): ${skipped}`
        );
        await this.loadData();
      } catch (e) {
        this.showErrorSnackbar(e?.message || "Ошибка импорта");
      } finally {
        this.isImporting = false;
      }
    },
    async saveRow(row) {
      const bim_room_id = row.bim_room_id;
      this.savingIds = { ...this.savingIds, [bim_room_id]: true };
      try {
        await saveBimRoom({
          bim_room_id,
          room_id: this.rowRoomIds[bim_room_id] ?? null,
        });
        this.showSucceedSnackbar("Сохранено");
        await this.loadData();
      } catch (e) {
        this.showErrorSnackbar(e?.message || "Ошибка сохранения");
      } finally {
        const next = { ...this.savingIds };
        delete next[bim_room_id];
        this.savingIds = next;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.bim-rooms {
  .top-bar {
    margin-bottom: 12px;
  }
  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    flex-wrap: wrap;
    gap: 8px;
    &__count {
      color: #666;
      font-size: 14px;
    }
    &__actions {
      display: flex;
      gap: 8px;
    }
  }
  .hidden-input {
    display: none;
  }
}
</style>
